/**
 * C5 -- the bank points at the rows it is answering with.
 *
 * The answer form offered four tick boxes and a note. A supervisor asks
 * about a particular control, a particular document, a particular
 * sealed decision, and the bank could not point at any of them; the
 * pack that went back cited whatever the four templated answers happen
 * to cite, or -- for a note-only reply -- nothing at all.
 *
 * Searching is how somebody finds a control or a document here already
 * (P67), so citing reuses it rather than inventing three browsers. An
 * entity is cited through its SEALED THREADS: pick the service, then
 * pick which of its chains to send. Those are the citations that make
 * the pack verify against what it cites rather than merely list it.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { useSearch, type SearchHit } from "shared/search/useSearch";

import { Identifier } from "shared/components/Identifier";
import { statusLabel } from "shared/vocab/workflowState";
export interface Citation {
  kind: "control" | "evidence" | "chain";
  ref: string;
  /** For display only; the server resolves the row it will carry. */
  label: string;
}

interface CitableChain {
  slug: string;
  ref: string;
  rows: number;
  latest_captured_at: string;
}

/**
 * Round 10 F4 -- the chip stopped mid-word.
 *
 * A hard `slice(0, 60)` cut "formal practices f", which reads as a
 * rendering fault rather than as a shortened sentence. Cut at the last
 * word boundary before the limit and mark the cut, so the reader can
 * see that there is more rather than wondering what happened.
 */
export function clip(text: string, limit = 60): string {
  const trimmed = text.trim();
  if (trimmed.length <= limit) return trimmed;
  const cut = trimmed.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  // A single word longer than the limit has no boundary to cut on;
  // cutting it short is still better than carrying the whole thing.
  const head = lastSpace > limit * 0.5 ? cut.slice(0, lastSpace) : cut;
  return `${head.replace(/[\s.,;:]+$/, "")}\u2026`;
}

/** Search kinds that resolve to a citation, and how. */
const DIRECT: Record<string, "control" | "evidence"> = {
  framework_control: "control",
  evidence_artefact: "evidence",
};
/** Kinds whose citation is one of their sealed threads. */
const SEALED = ["service", "ibs_service", "capability", "vendor", "configuration_item"];
const KINDS = [...Object.keys(DIRECT), ...SEALED].join(",");

/**
 * Round 10 F4 -- what the code IS, before it is cited.
 *
 * Round 8 put the control's own text on the row, which settled "which
 * 3.1.1-CR7". The question after that one is the examiner's: has this
 * been graded, in which cycle, and is the judgement sealed. The
 * product has answered all three since round 5 -- the resolve endpoint
 * returns exactly what the pack will carry -- and the picker never
 * asked it, so the bank chose a citation and found out what it said
 * when the zip was opened.
 *
 * Resolved through the SAME composer the pack uses, deliberately: a
 * second rendering of the same facts is a second thing to drift.
 */
function CitationPreview({
  kind,
  // NOT named `ref`: React reserves that prop, and passing a string to
  // it renders nothing and throws "function components cannot have
  // string refs" -- which tsc accepts, because the legacy string-ref
  // type is still in the JSX signature. Found by running the test.
  citationRef,
}: {
  kind: "control" | "evidence";
  citationRef: string;
}) {
  const ref = citationRef;
  const { t } = useTranslation();
  const preview = useQuery<ResolvedCitation[]>({
    queryKey: ["assurance", "citation-preview", kind, ref],
    queryFn: () =>
      api.post<ResolvedCitation[]>("/v1/assurance/citations/resolve", {
        citations: [{ kind, ref }],
      }),
    staleTime: 60_000,
    retry: false,
  });
  if (preview.isLoading) {
    return (
      <p className="ms-4 mt-1 text-[11px] text-slate-400">
        {t("assurance.rfi.cite.preview.loading")}
      </p>
    );
  }
  const row = preview.data?.[0];
  if (!row) {
    return (
      <p
        data-testid={`cite-preview-error-${ref}`}
        className="ms-4 mt-1 text-[11px] text-amber-800"
      >
        {t("assurance.rfi.cite.preview.failed")}
      </p>
    );
  }
  const detail = (row.detail ?? {}) as Record<string, unknown>;
  const assessment = detail.assessment as Record<string, unknown> | null | undefined;
  return (
    <dl
      data-testid={`cite-preview-${ref}`}
      className="ms-4 mt-1 space-y-0.5 text-[11px] text-slate-600"
    >
      {assessment ? (
        <>
          <div>
            {t("assurance.rfi.cite.preview.cycle")}{" "}
            <span className="text-slate-800">{String(assessment.cycle ?? "")}</span>
          </div>
          <div>
            {t("assurance.rfi.cite.preview.grade")}{" "}
            <span className="text-slate-800">
              {statusLabel(String(assessment.status ?? ""))}
            </span>
            {assessment.current_ml != null ? (
              <span className="text-slate-500">
                {" "}
                {t("assurance.rfi.cite.preview.maturity", {
                  current: String(assessment.current_ml),
                  target: String(assessment.target_ml ?? "-"),
                })}
              </span>
            ) : null}
          </div>
        </>
      ) : (
        <div data-testid={`cite-preview-ungraded-${ref}`}>
          {t("assurance.rfi.cite.preview.notGraded")}
        </div>
      )}
      <div>
        {detail.sealed ? (
          <span className="text-emerald-800">
            {t("assurance.rfi.cite.preview.sealed")}
          </span>
        ) : (
          <span className="text-amber-800">
            {String(detail.seal_note ?? t("assurance.rfi.cite.preview.notSealed"))}
          </span>
        )}
      </div>
    </dl>
  );
}

/** What the resolve endpoint hands back, as much of it as this needs. */
interface ResolvedCitation {
  kind: string;
  ref: string;
  label: string;
  detail: Record<string, unknown>;
}

function ChainOptions({
  hit,
  onCite,
}: {
  hit: SearchHit;
  onCite: (c: Citation) => void;
}) {
  const { t } = useTranslation();
  const chains = useQuery<CitableChain[]>({
    queryKey: ["assurance", "citable-chains", hit.kind, hit.id],
    queryFn: () =>
      api.get<CitableChain[]>(
        `/v1/assurance/citable-chains?entity_kind=${hit.kind}&entity_id=${hit.id}`,
      ),
  });
  if (chains.isPending) return null;
  const rows = chains.data ?? [];
  if (rows.length === 0)
    // Said plainly. Nothing sealed is a real answer about this record,
    // and dropping the row silently reads as a surface that failed.
    return (
      <p
        data-testid={`cite-nothing-sealed-${hit.id}`}
        className="text-xs text-slate-500"
      >
        {t("assurance.rfi.cite.nothingSealed")}
      </p>
    );
  return (
    <ul className="mt-1 space-y-0.5">
      {rows.map((c) => (
        <li key={c.ref} className="flex items-center gap-2 text-xs">
          <button
            type="button"
            data-testid={`cite-chain-${c.slug}-${hit.id}`}
            onClick={() =>
              onCite({
                kind: "chain",
                ref: c.ref,
                label: `${hit.display_name} · ${c.slug}`,
              })
            }
            className="font-medium text-brand-brassDark hover:underline"
          >
            {t("assurance.rfi.cite.add")}
          </button>
          <span className="text-slate-600">
            {t("assurance.rfi.cite.sealedRows", {
              slug: c.slug,
              count: c.rows,
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function CitePicker({
  chosen,
  onChange,
}: {
  chosen: Citation[];
  onChange: (next: Citation[]) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [openHit, setOpenHit] = useState<string | null>(null);

  // The same search the reader already uses to find a control or a
  // document, with the kinds narrowed to what can be cited.
  const results = useSearch({
    q: q.trim().length >= 2 ? q : "",
    kinds: KINDS,
    limit: 8,
  });

  const add = (c: Citation) => {
    if (chosen.some((x) => x.kind === c.kind && x.ref === c.ref)) return;
    onChange([...chosen, c]);
  };

  return (
    <div data-testid="assurance-rfi-cite" className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">
        {t("assurance.rfi.cite.heading")}
      </label>
      {chosen.length > 0 ? (
        <ul className="flex flex-wrap gap-1">
          {chosen.map((c) => (
            <li
              key={`${c.kind}:${c.ref}`}
              data-testid={`cite-chosen-${c.kind}`}
              className="flex items-center gap-1 rounded bg-white px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200"
            >
              {c.label}
              <button
                type="button"
                aria-label={t("assurance.rfi.cite.remove")}
                onClick={() =>
                  onChange(
                    chosen.filter((x) => !(x.kind === c.kind && x.ref === c.ref)),
                  )
                }
                className="text-slate-400 hover:text-rose-600"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <input
        dir="auto"
        data-testid="assurance-rfi-cite-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t("assurance.rfi.cite.placeholder")}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
      />
      {q.trim().length >= 2 && results.data ? (
        <ul data-testid="assurance-rfi-cite-results" className="space-y-1">
          {results.data.results.map((h) => (
            <li key={`${h.kind}:${h.id}`} className="text-xs">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase text-slate-400">
                  {h.kind.replace(/_/g, " ")}
                </span>
                <span className="text-slate-700">
                  <Identifier>{h.display_name}</Identifier>
                </span>
                {/* D3 (round 8) -- what the code IS.
                    The picker listed eight bare codes and a Cite
                    button, so choosing between 3.1.1-CR7 and
                    3.3.1-CR7 meant leaving the page to find out which
                    was which -- and a wrong citation inside a signed
                    pack is worse than uncited prose, because it looks
                    like an answer and it is sealed. The search has
                    returned the control's own text all along. */}
                {h.snippet ? (
                  <span
                    data-testid={`cite-subtitle-${h.id}`}
                    className="min-w-0 flex-1 truncate text-slate-500"
                    title={h.snippet}
                    dir="auto"
                  >
                    {h.snippet}
                  </span>
                ) : null}
                {DIRECT[h.kind] ? (
                  <button
                    type="button"
                    data-testid={`cite-check-${h.id}`}
                    onClick={() => setOpenHit(openHit === h.id ? null : h.id)}
                    className="text-slate-500 hover:underline"
                  >
                    {t("assurance.rfi.cite.preview.check")}
                  </button>
                ) : null}
                {DIRECT[h.kind] ? (
                  <button
                    type="button"
                    data-testid={`cite-add-${h.id}`}
                    onClick={() =>
                      add({
                        kind: DIRECT[h.kind]!,
                        ref: h.id,
                        // D3 -- the chip, and the confirm panel that
                        // repeats it, say what the code is. A chip
                        // reading "3.1.1-CR7" is the same bare code the
                        // list showed, one step later and with nothing
                        // left to check it against.
                        label: h.snippet
                          ? `${h.display_name} -- ${clip(h.snippet)}`
                          : h.display_name,
                      })
                    }
                    className="font-medium text-brand-brassDark hover:underline"
                  >
                    {t("assurance.rfi.cite.add")}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`cite-sealed-${h.id}`}
                    onClick={() => setOpenHit(openHit === h.id ? null : h.id)}
                    className="font-medium text-brand-brassDark hover:underline"
                  >
                    {t("assurance.rfi.cite.sealedRecords")}
                  </button>
                )}
              </div>
              {openHit === h.id ? (
                DIRECT[h.kind] ? (
                  <CitationPreview kind={DIRECT[h.kind]!} citationRef={h.id} />
                ) : (
                  <ChainOptions hit={h} onCite={add} />
                )
              ) : null}
            </li>
          ))}
          {results.data.results.length === 0 ? (
            <li className="text-xs text-slate-500">
              {t("assurance.rfi.cite.noResults")}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
